@if (open()) {
  <div class="cx-overlay" (click)="close()">
    <div class="cx-modal" role="dialog" aria-modal="true" aria-labelledby="cx-title"
         tabindex="-1" (click)="stop($event)">

      <!-- ── Header ── fixed across both panes, so the modal never resizes ── -->
      <header class="cx-head">
        <div class="cx-head__titles">
          <h2 class="cx-head__title" id="cx-title">Connections</h2>
          <p class="cx-head__sub">Link your accounts so agents work with your access, not a shared login.</p>
        </div>
        <button type="button" class="cx-btn cx-btn--ghost"
                [disabled]="verifying() || loading()" (click)="refresh()"
                title="Re-check every connection against the token vault">
          <i class="pi pi-refresh" [class.pi-spin]="verifying()"></i> Refresh
        </button>
        <button type="button" class="cx-iconbtn" (click)="close()" aria-label="Close connections">
          <i class="pi pi-times"></i>
        </button>
      </header>

      <!-- ── Sliding track: list ⇄ detail ── -->
      <div class="cx-stage">
        <div class="cx-track" [class.cx-track--detail]="showDetail()">

          <!-- Pane 1 — every connector. `inert` as well as aria-hidden: the
               off-screen pane must not be reachable by Tab either. -->
          <section class="cx-pane" [attr.aria-hidden]="showDetail()"
                   [attr.inert]="showDetail() ? '' : null">
            @if (loading()) {
              <div class="cx-state"><i class="pi pi-spinner pi-spin"></i> Loading connections…</div>
            } @else if (error()) {
              <div class="cx-state cx-state--error" role="alert">
                <i class="pi pi-exclamation-circle"></i>
                <span>Couldn't load your connections.</span>
                <button type="button" class="cx-btn cx-btn--ghost" (click)="load(true)">
                  <i class="pi pi-refresh"></i> Retry
                </button>
              </div>
            } @else if (empty()) {
              <div class="cx-state">
                <i class="pi pi-link"></i>
                <span>Per-user connections aren't enabled in this environment yet.</span>
              </div>
            } @else {
              <div class="cx-filters" role="tablist" aria-label="Filter connections">
                <button type="button" class="cx-seg" role="tab"
                        [class.cx-seg--on]="filter() === 'all'"
                        [attr.aria-selected]="filter() === 'all'" (click)="setFilter('all')">
                  All <span class="cx-seg__n">{{ connectors().length }}</span>
                </button>
                <button type="button" class="cx-seg" role="tab"
                        [class.cx-seg--on]="filter() === 'connected'"
                        [attr.aria-selected]="filter() === 'connected'" (click)="setFilter('connected')">
                  Connected <span class="cx-seg__n">{{ connectedCount() }}</span>
                </button>
                <button type="button" class="cx-seg" role="tab"
                        [class.cx-seg--on]="filter() === 'off'"
                        [attr.aria-selected]="filter() === 'off'" (click)="setFilter('off')">
                  Not connected <span class="cx-seg__n">{{ notConnectedCount() }}</span>
                </button>
              </div>

              <div class="cx-list">
                @if (rows().length === 0) {
                  <p class="cx-list__empty">Nothing here — try another filter.</p>
                } @else {
                  <div class="cx-rows">
                    @for (c of rows(); track c.name) {
                      <!-- Two sibling buttons, never nested: the row body opens
                           the detail pane, the trailing action connects. -->
                      <div class="cx-row" [class.cx-row--soon]="isSoon(c)">
                        <button type="button" class="cx-row__main" (click)="select(c)"
                                [attr.aria-label]="c.displayName + ' — ' + statusLabel(c) + ', open details'">
                          <span class="cx-tile" [class.cx-tile--dim]="!isConnected(c)">
                            <app-brand-mark [name]="c.name" [label]="c.displayName" [size]="20" />
                          </span>
                          <span class="cx-row__name">{{ c.displayName }}</span>
                          @if (isByo(c) && isConnected(c) && c.connection?.fingerprint) {
                            <span class="cx-fp" title="Token fingerprint">•••• {{ c.connection?.fingerprint }}</span>
                          }
                          @if (isConnected(c) || isSoon(c) || isRevoked(c)) {
                            <span class="cx-status cx-status--{{ statusKind(c) }}">
                              <span class="cx-dot"></span>{{ statusLabel(c) }}
                            </span>
                          }
                          @if (isConnected(c) || isSoon(c)) {
                            <i class="pi pi-angle-right cx-row__chev"></i>
                          }
                        </button>
                        @if (!isConnected(c) && !isSoon(c)) {
                          @if (isByo(c)) {
                            <!-- BYO: no consent round-trip to open — the CTA
                                 opens the token paste form instead. -->
                            <button type="button" class="cx-btn cx-row__cta"
                                    [class.cx-btn--warn]="isRevoked(c)"
                                    [class.cx-btn--outline]="!isRevoked(c)"
                                    (click)="openCred(c, $event)">
                              Add token
                            </button>
                          } @else {
                            <button type="button" class="cx-btn cx-row__cta"
                                    [class.cx-btn--warn]="isRevoked(c)"
                                    [class.cx-btn--outline]="!isRevoked(c)"
                                    [disabled]="busy() === c.name" (click)="connect(c)">
                              @if (busy() === c.name) {
                                <i class="pi pi-spinner pi-spin"></i> Opening…
                              } @else {
                                {{ connectLabel(c) }}
                              }
                            </button>
                          }
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </section>

          <!-- Pane 2 — one connector in detail -->
          <section class="cx-pane" [attr.aria-hidden]="!showDetail()"
                   [attr.inert]="showDetail() ? null : ''">
            @if (detail(); as c) {
              <div class="cx-detail__head">
                <button type="button" class="cx-iconbtn cx-iconbtn--outline" (click)="back()"
                        aria-label="Back to all connections">
                  <i class="pi pi-angle-left"></i>
                </button>
                <span class="cx-tile cx-tile--lg" [class.cx-tile--dim]="!isConnected(c)">
                  <app-brand-mark [name]="c.name" [label]="c.displayName" [size]="22" />
                </span>
                <span class="cx-detail__name">{{ c.displayName }}</span>
                @if (isByo(c) && isConnected(c) && c.connection?.fingerprint) {
                  <span class="cx-fp" title="Token fingerprint">•••• {{ c.connection?.fingerprint }}</span>
                }
                <span class="cx-pill cx-pill--{{ statusKind(c) }}">
                  <span class="cx-dot"></span>{{ statusLabel(c) }}
                </span>
              </div>

              <div class="cx-detail__body">
                @if (c.description) {
                  <p class="cx-detail__desc">{{ c.description }}</p>
                }

                <div class="cx-rule"><span>{{ scopeTitle(c) }}</span></div>
                <ul class="cx-scopes">
                  @for (s of permissionLines(c); track s) {
                    <li class="cx-scope">
                      <i class="pi pi-check"></i>
                      <span [class.cx-scope__raw]="isRawLine(c, s)">{{ s }}</span>
                    </li>
                  } @empty {
                    @if (isByo(c) && isReady(c)) {
                      <!-- BYO connectors carry no OAuth scopes: access is
                           whatever the token the user creates can do. -->
                      <li class="cx-scope cx-scope--none">
                        <i class="pi pi-key"></i>
                        <span>Access matches the token you create — you control it in {{ c.displayName }}.</span>
                      </li>
                    } @else {
                      <li class="cx-scope cx-scope--none">
                        <i class="pi pi-clock"></i>
                        <span>Permissions are published when this connector goes live.</span>
                      </li>
                    }
                  }
                </ul>
                <p class="cx-note">
                  Nothing beyond this list is granted. {{ c.displayName }} enforces its own
                  permissions on top — an agent can never reach anything you can't open yourself.
                </p>

                <!-- Dev-sandbox onboarding: rendered only when the registry
                     publishes a packaged install for this connector. The
                     connector's OAuth app is a packaged External Client App
                     authored in the Dev Hub; it must be installed in the
                     DEV'S OWN sandbox before consent can complete there. -->
                @if (c.sandboxInstall; as si) {
                  <div class="cx-rule"><span>CONNECTING YOUR OWN SANDBOX?</span></div>
                  <p class="cx-note">
                    Install the connect app into your sandbox once, then come back and hit
                    Connect. Copy-paste in a terminal with the Salesforce CLI (replace
                    <code class="cx-sandbox__alias">YOUR_SANDBOX_ALIAS</code> with your org alias):
                  </p>
                  <div class="cx-sandbox">
                    <code class="cx-sandbox__cmd">{{ sandboxCmd(c) }}</code>
                    <button type="button" class="cx-sandbox__copy" (click)="copySandboxCmd(c)"
                            aria-label="Copy the install command">
                      <i class="pi pi-copy"></i>
                    </button>
                  </div>
                  <p class="cx-note cx-note--sandbox">
                    No CLI handy?
                    <a class="cx-link" [href]="sandboxInstallUrl(c)" target="_blank"
                       rel="noopener noreferrer">Install in the browser
                       <i class="pi pi-external-link"></i></a>
                    — log in with your sandbox username.
                    @if (si.note) {
                      {{ si.note }}
                    }
                  </p>
                }

                <!-- Post-disconnect: the platform side is severed (agents stop
                     within a minute), but no vendor here offers app-initiated
                     revocation — withdrawing the approval recorded on the
                     vendor's side is a step only the user can take. -->
                @if (showProviderRevoke(c)) {
                  <div class="cx-rule"><span>ONE MORE STEP (OPTIONAL)</span></div>
                  <p class="cx-note cx-note--revoke">
                    <i class="pi pi-info-circle"></i>
                    @if (isByo(c)) {
                      <!-- BYO: our stored copy is deleted, but the token the
                           user minted still exists on the vendor's side. -->
                      <span>
                        Removed — the stored token was deleted and agents can no longer use it.
                        The token you created still exists in {{ c.displayName }} until you delete
                        it there,
                        @if (c.helpUrl) {
                          under <a class="cx-link" [href]="c.helpUrl" target="_blank"
                                   rel="noopener noreferrer">your API tokens
                                   <i class="pi pi-external-link"></i></a>.
                        } @else {
                          in your {{ c.displayName }} account settings.
                        }
                      </span>
                    } @else {
                      <span>
                        Disconnected — agents can no longer use this connection. The approval you
                        granted on {{ c.displayName }}'s side stays on file with them until you remove
                        it there,
                        @if (providerRevokeUrl(c); as url) {
                          under <a class="cx-link" [href]="url" target="_blank"
                                   rel="noopener noreferrer">connected apps
                                   <i class="pi pi-external-link"></i></a>.
                        } @else {
                          under “connected apps” in your {{ c.displayName }} account settings.
                        }
                      </span>
                    }
                  </p>
                }

                @if (isConnected(c)) {
                  <div class="cx-rule"><span>THIS CONNECTION</span></div>
                  <dl class="cx-meta">
                    <div class="cx-meta__row">
                      <dt>Linked on</dt><dd>{{ fmtDate(c.connection?.connectedAt ?? null) }}</dd>
                    </div>
                    <div class="cx-meta__row">
                      <dt>Last verified</dt><dd>{{ fmtAgo(c.connection?.lastVerifiedAt ?? null) }}</dd>
                    </div>
                  </dl>
                }
              </div>
            }
          </section>

        </div>
      </div>

      <!-- ── Footer ── one row, swapped by context: never a second dialog ── -->
      <footer class="cx-foot">
        @if (showDetail() && detail(); as c) {
          @if (confirming()) {
            @if (isByo(c)) {
              <span class="cx-foot__ask">Remove {{ c.displayName }}? The stored token is deleted and agents stop using it within a minute.</span>
              <button type="button" class="cx-btn cx-btn--ghost" (click)="cancelDisconnect()">Cancel</button>
              <button type="button" class="cx-btn cx-btn--danger"
                      [disabled]="busy() === c.name" (click)="disconnect(c)">Yes, remove</button>
            } @else {
              <span class="cx-foot__ask">Disconnect {{ c.displayName }}? Agents stop using it within a minute.</span>
              <button type="button" class="cx-btn cx-btn--ghost" (click)="cancelDisconnect()">Cancel</button>
              <button type="button" class="cx-btn cx-btn--danger"
                      [disabled]="busy() === c.name" (click)="disconnect(c)">Yes, disconnect</button>
            }
          } @else if (isConnected(c) && isByo(c)) {
            <span class="cx-foot__hint">Swap in a fresh token any time, or remove it to stop agents using {{ c.displayName }}.</span>
            <button type="button" class="cx-btn cx-btn--outline"
                    (click)="openCred(c)">Rotate token</button>
            <button type="button" class="cx-btn cx-btn--outline cx-btn--quiet-danger"
                    [disabled]="busy() === c.name" (click)="askDisconnect()">Remove</button>
          } @else if (isConnected(c)) {
            <span class="cx-foot__hint">Stop agents using your {{ c.displayName }} access.</span>
            <button type="button" class="cx-btn cx-btn--outline cx-btn--quiet-danger"
                    [disabled]="busy() === c.name" (click)="askDisconnect()">Disconnect</button>
          } @else if (isSoon(c)) {
            <p class="cx-foot__note">
              <i class="pi pi-clock"></i>
              <span>Not available to link yet — we'll announce it when it ships.</span>
            </p>
          } @else if (isByo(c)) {
            <span class="cx-foot__hint">
              You create a token in {{ c.displayName }} and paste it here — it's checked with
              {{ c.displayName }}, then stored in the encrypted vault.
            </span>
            <button type="button" class="cx-btn cx-btn--primary" (click)="openCred(c)">
              Add token
            </button>
          } @else {
            <span class="cx-foot__hint">
              Opens {{ c.displayName }}'s own consent screen. You approve it there and come straight back here.
            </span>
            <button type="button" class="cx-btn cx-btn--primary"
                    [disabled]="busy() === c.name" (click)="connect(c)">
              @if (busy() === c.name) {
                <i class="pi pi-spinner pi-spin"></i> Opening consent…
              } @else {
                {{ connectLabel(c) }} {{ c.displayName }}
              }
            </button>
          }
        } @else {
          <p class="cx-foot__note">
            <i class="pi pi-lock"></i>
            <span>Agents act as you and see only what you can see. Tokens live in an encrypted
              vault — never in your browser, never in an agent's prompt. Disconnect any time.</span>
          </p>
        }
      </footer>

    </div>

    @if (toast(); as t) {
      <div class="cx-toast" role="status"><i class="pi pi-check-circle"></i>{{ t }}</div>
    }
  </div>

  <!-- ── BYO token form ── its own overlay/card (sibling of the modal's
       overlay, so backdrop clicks close only this layer). Reachable ONLY from
       the Add/Rotate buttons above — no route or deep link leads here. -->
  @if (credOpen() && credConnector(); as c) {
    <div class="cx-cred-overlay" (click)="closeCred()">
      <div class="cx-cred-card" role="dialog" aria-modal="true" aria-labelledby="cx-cred-title"
           tabindex="-1" (click)="stop($event)">

        <header class="cx-cred-head">
          <span class="cx-tile">
            <app-brand-mark [name]="c.name" [label]="c.displayName" [size]="20" />
          </span>
          <h3 class="cx-cred-title" id="cx-cred-title">
            {{ credRotating() ? 'Rotate your' : 'Add your' }} {{ c.displayName }} token
          </h3>
          <button type="button" class="cx-iconbtn" (click)="closeCred()"
                  [disabled]="credSaving()" aria-label="Close token form">
            <i class="pi pi-times"></i>
          </button>
        </header>

        <form class="cx-cred-body" (submit)="onCredSubmit($event)">
          @if (c.credentialHelp) {
            <p class="cx-cred-help">{{ c.credentialHelp }}</p>
          } @else {
            <p class="cx-cred-help">
              Paste an API token from your own {{ c.displayName }} account — agents will use it
              to act as you.
            </p>
          }
          @if (c.helpUrl) {
            <a class="cx-link cx-cred-create" [href]="c.helpUrl" target="_blank" rel="noopener noreferrer">
              Create a token <i class="pi pi-external-link"></i>
            </a>
          }

          @if (credNeedsEmail()) {
            <label class="cx-cred-label" for="cx-cred-email">{{ c.displayName }} account email</label>
            <input id="cx-cred-email" class="cx-cred-input" type="email"
                   autocomplete="off" spellcheck="false" [disabled]="credSaving()"
                   [value]="credEmail()" (input)="onCredEmailInput($event)" />
          }

          <label class="cx-cred-label" for="cx-cred-token">API token</label>
          <!-- type=password: the secret is masked while typed and never echoed
               back anywhere; the signal behind it is scrubbed on close. -->
          <input id="cx-cred-token" class="cx-cred-input cx-cred-input--token" type="password"
                 autocomplete="off" spellcheck="false" [disabled]="credSaving()"
                 placeholder="Paste your token" [value]="credToken()" (input)="onCredTokenInput($event)" />

          @if (credError(); as e) {
            <p class="cx-cred-error" role="alert">
              <i class="pi pi-exclamation-circle"></i><span>{{ e }}</span>
            </p>
          }

          <p class="cx-cred-phish">
            <i class="pi pi-shield"></i>
            <span>Agent Hub will never ask for this token by email or chat message.</span>
          </p>

          <footer class="cx-cred-foot">
            <button type="button" class="cx-btn cx-btn--ghost"
                    [disabled]="credSaving()" (click)="closeCred()">Cancel</button>
            <button type="submit" class="cx-btn cx-btn--primary" [disabled]="!canSaveCred()">
              @if (credSaving()) {
                <i class="pi pi-spinner pi-spin"></i> Checking…
              } @else {
                Save
              }
            </button>
          </footer>
        </form>

      </div>
    </div>
  }
}
